import { useState, useRef, CSSProperties } from 'react'
import { CircularProgress } from '@mui/material'
import { useQuery, QueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { Button } from '@components/v2/button'
import { getListExport } from '@global/api'
import { t } from '@lingui/core/macro'

/**
 * Generates a download link for a given string of data and programmatically clicks it to initiate the download.
 * The file will be downloaded with a name based on the provided `listId`.
 *
 * @param {Object} params Parameters for generating and clicking the download link.
 * @param {string} params.data The string data to be downloaded.
 * @param {string} params.listId The identifier for the list, used to create the file name.
 */
function generateAndClickDownloadLink({ data, listId }: { data: string; listId: string }) {
  const url = window.URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')
  link.href = url
  const fileName = `list-export-${listId}.csv`
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Returns a promise that resolves after a specified delay. This function is intended to simulate a delayed start
 * for operations such as file downloads. The actual promise resolution occurs after ensuring a minimum elapsed time
 * to prevent a UI flash for quick operations.
 *
 * @param {Object} params Parameters for the delayed start.
 * @param {number} params.delay The initial delay (in milliseconds) before starting the timer.
 * @param {React.MutableRefObject<number>} params.timer A React ref object containing the start time (as a timestamp).
 *                                                       Used to calculate the elapsed time.
 * @returns {Promise<void>} A promise that resolves after the specified delays and conditions are met.
 */
function delayedStartPromise({ delay, timer }): Promise<void> {
  return new Promise(resolve => {
    // Initial delay before starting the timer for the promise
    setTimeout(() => {
      const currentTime = Date.now()
      const elapsedTime = currentTime - timer.current
      const waitUntilSeconds = 1500

      // Wait at least X seconds before allowing the download to start.
      if (elapsedTime < waitUntilSeconds) return

      // Set another timeout inside the first one to resolve the promise after simulating a download time.
      setTimeout(() => {
        resolve()
      }, waitUntilSeconds)
    }, delay)
  })
}

const emptyFn = () => {
  // do nothing
}

export const ExportList = ({ listId, ...rest }: { listId: string; style?: CSSProperties }) => {
  const queryClient = new QueryClient()
  const [startDownload, setStartDownload] = useState(false)
  const timer = useRef(0)
  const QUERY_KEY = ['list-export', listId]

  // FIXME: After upgrading React Query to v4+.We should be able to do
  // a minor refactor here using invalidateQueries, to reset data to
  // undefined to allow a simpler re-download trigger.
  const { refetch, isLoading } = useQuery<string | null>({
    queryKey: QUERY_KEY,
    queryFn: () => getListExport({ listId }),
    enabled: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

  // Simulate a download time of at least X seconds,
  // so the fast download is not a flash of the UI.
  // This slower transition is a more pleasing experience.
  if (isLoading && !timer.current) {
    timer.current = Date.now()
  }

  const downloadFile = async () => {
    setStartDownload(true)

    const { data: dataFromClick, error } = await refetch()

    if (dataFromClick) {
      void delayedStartPromise({ delay: 2000, timer }).then(() => {
        generateAndClickDownloadLink({ data: dataFromClick, listId })
        void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
        timer.current = 0
        setStartDownload(false)
      })

      return
    }

    if (error) {
      console.log('Error fetching list export:', error)
    }
  }

  return (
    <Button
      onClick={!startDownload ? downloadFile : emptyFn}
      variant="secondary"
      aria-describedby="export-button"
      className="h-[inherit]"
      data-testid="export-list-button"
      {...rest}
    >
      {startDownload ? <CircularProgress size={20} /> : <Download size={18} />}
      {t`Export list`}
    </Button>
  )
}
